import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, ChevronRight } from "lucide-react";

export const StageOptionsList = ({ shuffledOptions, selectedOption, onSelect }) => (
  <RadioGroup value={selectedOption} onValueChange={onSelect}>
    <div className="space-y-3">
      {shuffledOptions.map(([element, option]) => {
        const isSelected = selectedOption === element;
        return (
          <div
            key={element}
            data-testid={`option-${element}`}
            className={`flex items-start space-x-3 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
              isSelected ? "shadow-md" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow"
            }`}
            style={isSelected ? { borderColor: '#FF751F', backgroundColor: '#FFF8F0' } : {}}
            onClick={() => onSelect(element)}
          >
            <RadioGroupItem value={element} id={element} className="mt-1 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <Label htmlFor={element} className="text-sm sm:text-base text-gray-800 leading-relaxed cursor-pointer block">
                {option.text}
              </Label>
            </div>
          </div>
        );
      })}
    </div>
  </RadioGroup>
);

export const ReviewModeNotice = ({ hasExistingChoice, currentStage }) => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
    <p className="text-sm text-blue-700">
      {hasExistingChoice ? (
        <>
          <strong>Review Mode:</strong> You are viewing a previously answered question.
          {currentStage < 3 ? " Changing your response will require re-answering subsequent questions in this scenario." : ""}
        </>
      ) : (
        <>
          <strong>Review Mode:</strong> You need to submit a fresh response for this stage as you have taken a new path with your modified response while reviewing.
        </>
      )}
    </p>
  </div>
);

export const AssessmentNavigation = ({
  canGoBack,
  canSkipToEnd,
  canSubmit,
  submitting,
  isLastQuestion,
  onBack,
  onSkipToEnd,
  onNext,
}) => (
  <div className="flex justify-between gap-2 pt-4">
    <Button
      data-testid="back-btn"
      onClick={onBack}
      disabled={!canGoBack || submitting}
      variant="outline"
      className="h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base border-2 flex-shrink-0"
    >
      <ArrowLeft className="mr-1 sm:mr-2 w-4 h-4" />
      Back
    </Button>

    {canSkipToEnd && (
      <Button
        data-testid="skip-to-end-btn"
        onClick={onSkipToEnd}
        variant="outline"
        className="h-10 sm:h-12 px-4 sm:px-6 text-sm sm:text-base border-2"
        style={{ borderColor: '#FF751F', color: '#FF751F' }}
      >
        <span className="hidden sm:inline">Skip to End</span>
        <span className="sm:hidden">Skip</span>
        <ChevronRight className="ml-1 w-4 h-4" />
      </Button>
    )}

    <Button
      data-testid="next-btn"
      onClick={onNext}
      disabled={!canSubmit || submitting}
      className="h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base bg-gradient-to-r from-[#FF751F] to-[#FFBD25] hover:from-[#FF751F] hover:to-[#FFD04A] text-white shadow-lg font-semibold flex-1 sm:flex-initial"
    >
      {submitting ? "Submitting..." : (
        <>
          <span className="hidden sm:inline">{isLastQuestion ? "Complete Assessment" : "Next"}</span>
          <span className="sm:hidden">{isLastQuestion ? "Complete" : "Next"}</span>
          <ArrowRight className="ml-1 sm:ml-2 w-4 h-4" />
        </>
      )}
    </Button>
  </div>
);
